import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  CheckCircle,
  Clock,
  Users,
  Building2,
  TrendingUp,
  Target,
  X,
  Copy
} from 'lucide-react';
import { supabase } from '../lib/auth';
import { useAuth } from '../components/AuthProvider';
import { updateAppointmentDateTimeAndStatus, getActiveSDRs } from '../lib/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import Modal from '../components/ui/Modal';
import RescheduleModal from '../components/ui/RescheduleModal';
import KpiCard from '../components/dayday/KpiCard';
import AppointmentList from '../components/dayday/AppointmentList';
import FiltersBar from '../components/dayday/FiltersBar';
import ConfettiEffect from '../components/dayday/ConfettiEffect';
import FeedBar from '../components/dayday/FeedBar';
import TeamProgressChart from '../components/dayday/TeamProgressChart';
import SdrProgressBars from '../components/dayday/SdrProgressBars';
import RdvDetailModal from '../components/dayday/RdvDetailModal';
import RdvListGrid from '../components/dayday/RdvListGrid';

interface AppointmentData {
  id: string;
  date: string;
  time: string;
  prospect_name: string | null;
  company_name: string | null;
  phone: string | null;
  job_title: string | null;
  status: string;
  client_id: string;
  client_name: string;
  sdr_id: string | null;
  sdr_name: string;
  created_at: string;
  prospect_linkedin?: string;
}

interface SdrCreatedStats {
  sdr_name: string;
  sdr_id: string;
  total_created: number;
  clients: Set<string>;
  client_names: string[];
  appointments: AppointmentData[];
}

interface ClientCreatedStats {
  client_name: string;
  client_id: string;
  total_created: number;
  sdr_names: string[];
  appointments: AppointmentData[];
}

const ConfirmationRDVsPageNew: React.FC = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [appointments, setAppointments] = useState<AppointmentData[]>([]);
  const [appointmentsCreatedToday, setAppointmentsCreatedToday] = useState<AppointmentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingCreated, setLoadingCreated] = useState(true);
  const [activeTab, setActiveTab] = useState<'confirmations' | 'stats'>('confirmations');
  const [showConfetti, setShowConfetti] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState<{
    show: boolean;
    message: string;
    appointment: AppointmentData | null;
  }>({
    show: false,
    message: '',
    appointment: null
  });
  const [rescheduleModal, setRescheduleModal] = useState<{
    show: boolean;
    appointment: AppointmentData | null;
    newStatus: string;
  }>({
    show: false,
    appointment: null,
    newStatus: ''
  });
  const [filterSdr, setFilterSdr] = useState<string>('all');
  const [filterClient, setFilterClient] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [selectedRdv, setSelectedRdv] = useState<AppointmentData | null>(null);
  const [rdvDetailModalOpen, setRdvDetailModalOpen] = useState(false);
  const [scheduledSdrs, setScheduledSdrs] = useState<Array<{
    id: string;
    name: string;
    client_id: string | null;
    client_name: string;
  }>>([]);

  useEffect(() => {
    if (!profile) return;
    if (profile.role !== 'admin' && profile.role !== 'super_admin') {
      navigate('/');
    }
  }, [profile, navigate]);

  useEffect(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    if (dayOfWeek === 0) {
      today.setDate(today.getDate() + 1);
      setSelectedDate(today);
    } else if (dayOfWeek === 6) {
      today.setDate(today.getDate() + 2);
      setSelectedDate(today);
    }
  }, []);

  const normalizeStatus = useCallback((status: string | null | undefined): string => {
    if (!status) return 'Scheduled';

    const statusMap: Record<string, string> = {
      scheduled: 'Scheduled',
      confirmed: 'Confirmed',
      confirmé: 'Confirmed',
      postponed: 'Postponed',
      reporté: 'Postponed',
      cancelled: 'Cancelled',
      annulé: 'Cancelled',
      completed: 'Completed',
      effectué: 'Completed',
      no_answer: 'No reply – VM/SMS',
      'no answer': 'No reply – VM/SMS',
      'no reply - vm/sms': 'No reply – VM/SMS',
      'no reply – vm/sms': 'No reply – VM/SMS'
    };

    const normalized = statusMap[status.toLowerCase()];
    if (normalized) return normalized;

    if (
      ['Scheduled', 'Confirmed', 'Postponed', 'Cancelled', 'Completed', 'No reply – VM/SMS'].includes(
        status
      )
    ) {
      return status;
    }

    return status;
  }, []);

  const fetchAppointments = useCallback(async () => {
    if (!selectedDate) return;
    setLoading(true);
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('appointments')
        .select(`
          id,
          date,
          time,
          prospect_name,
          company_name,
          phone,
          job_title,
          status,
          client_id,
          sdr_id,
          created_at,
          prospect_linkedin,
          clients!inner(name),
          profiles!appointments_sdr_id_fkey(sales_name)
        `)
        .eq('date', dateStr)
        .order('time', { ascending: true });

      if (error) throw error;

      const formatted = (data || []).map((apt: any) => ({
        id: apt.id,
        date: apt.date,
        time: apt.time,
        prospect_name: apt.prospect_name,
        company_name: apt.company_name,
        phone: apt.phone,
        job_title: apt.job_title,
        status: normalizeStatus(apt.status),
        client_id: apt.client_id,
        client_name: apt.clients?.name || 'N/A',
        sdr_id: apt.sdr_id,
        sdr_name: apt.profiles?.sales_name || 'N/A',
        created_at: apt.created_at,
        prospect_linkedin: apt.prospect_linkedin
      }));

      setAppointments(formatted);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, normalizeStatus]);

  const fetchAppointmentsCreatedToday = useCallback(async () => {
    if (!selectedDate) return;
    setLoadingCreated(true);
    try {
      const startOfDay = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate()
      ).toISOString();
      const endOfDay = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate() + 1
      ).toISOString();

      const { data, error } = await supabase
        .from('appointments')
        .select(`
          id,
          date,
          time,
          prospect_name,
          company_name,
          phone,
          job_title,
          status,
          client_id,
          sdr_id,
          created_at,
          prospect_linkedin,
          clients!inner(name),
          profiles!appointments_sdr_id_fkey(sales_name)
        `)
        .gte('created_at', startOfDay)
        .lt('created_at', endOfDay)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formatted = (data || []).map((apt: any) => ({
        id: apt.id,
        date: apt.date,
        time: apt.time,
        prospect_name: apt.prospect_name,
        company_name: apt.company_name,
        phone: apt.phone,
        job_title: apt.job_title,
        status: normalizeStatus(apt.status),
        client_id: apt.client_id,
        client_name: apt.clients?.name || 'N/A',
        sdr_id: apt.sdr_id,
        sdr_name: apt.profiles?.sales_name || 'N/A',
        created_at: apt.created_at,
        prospect_linkedin: apt.prospect_linkedin
      }));

      setAppointmentsCreatedToday(formatted);
    } catch (error) {
      console.error('Error fetching appointments created on selected date:', error);
    } finally {
      setLoadingCreated(false);
    }
  }, [selectedDate, normalizeStatus]);

  const fetchScheduledSdrs = useCallback(async () => {
    if (!selectedDate) return;
    try {
      const activeSDRs = await getActiveSDRs(selectedDate);

      const sdrs = activeSDRs.map((sdr) => ({
        id: sdr.sdr_id,
        name: sdr.sdr_name,
        client_id: sdr.client_id,
        client_name: sdr.client_name
      }));

      setScheduledSdrs(sdrs);
    } catch (error) {
      console.error('Error fetching scheduled SDRs:', error);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchAppointments();
    fetchAppointmentsCreatedToday();
    fetchScheduledSdrs();
  }, [fetchAppointments, fetchAppointmentsCreatedToday, fetchScheduledSdrs]);

  useEffect(() => {
    const dateStr = selectedDate.toISOString().split('T')[0];
    const channel = supabase
      .channel('appointments_realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'appointments',
          filter: `date=eq.${dateStr}`
        },
        async () => {
          await fetchAppointments();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedDate, fetchAppointments]);

  useEffect(() => {
    const channel = supabase
      .channel('appointments_created_today')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'appointments'
        },
        async () => {
          await fetchAppointmentsCreatedToday();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchAppointmentsCreatedToday]);

  const goToPreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    if (newDate.getDay() === 0) {
      newDate.setDate(newDate.getDate() - 2);
    } else if (newDate.getDay() === 6) {
      newDate.setDate(newDate.getDate() - 1);
    }
    setSelectedDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    if (newDate.getDay() === 0) {
      newDate.setDate(newDate.getDate() + 1);
    } else if (newDate.getDay() === 6) {
      newDate.setDate(newDate.getDate() + 2);
    }
    setSelectedDate(newDate);
  };

  const updateStatus = async (appointmentId: string, newStatus: string) => {
    try {
      const appointment = appointments.find((apt) => apt.id === appointmentId);
      if (!appointment) return;

      if (newStatus === 'Postponed') {
        setRescheduleModal({
          show: true,
          appointment,
          newStatus
        });
        return;
      }

      const { error } = await supabase
        .from('appointments')
        .update({ status: newStatus })
        .eq('id', appointmentId);

      if (error) throw error;

      if (newStatus === 'Confirmed') {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 2000);
      }

      await fetchAppointments();

      const message = generateConfirmationMessage({ ...appointment, status: newStatus });
      setConfirmationModal({
        show: true,
        message,
        appointment: { ...appointment, status: newStatus }
      });
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const generateConfirmationMessage = (apt: AppointmentData): string => {
    const formatDate = (dateStr: string) => {
      const date = new Date(dateStr);
      const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
      const months = [
        'janvier',
        'février',
        'mars',
        'avril',
        'mai',
        'juin',
        'juillet',
        'août',
        'septembre',
        'octobre',
        'novembre',
        'décembre'
      ];
      return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
    };

    const baseInfo = `🏢 Société : ${apt.company_name || 'N/A'}\n👥 Contact : ${
      apt.prospect_name || 'N/A'
    }\n📞 Téléphone : ${apt.phone || 'N/A'}`;

    switch (apt.status) {
      case 'Scheduled':
        return `📅 RDV planifié\n\n📆 Date : ${formatDate(apt.date)}\n⏰ Heure : ${apt.time}\n${baseInfo}`;
      case 'Confirmed':
        return `✅ RDV confirmé\n\n📆 Date : ${formatDate(apt.date)}\n⏰ Heure : ${apt.time}\n${baseInfo}`;
      case 'Postponed':
        return `📅 RDV reporté\n\n📆 Nouvelle date : ${formatDate(apt.date)}\n⏰ Nouvelle heure : ${
          apt.time
        }\n${baseInfo}`;
      case 'Cancelled':
        return `❌ RDV annulé\n\n${baseInfo}\n📆 Date initiale : ${formatDate(apt.date)} à ${apt.time}`;
      case 'No reply – VM/SMS':
      case 'No Answer':
        return `📞 Sans réponse - VM/SMS laissé\n\n📆 Date : ${formatDate(apt.date)}\n⏰ Heure : ${
          apt.time
        }\n${baseInfo}`;
      case 'Completed':
        return `✅ RDV effectué\n\n📆 Date : ${formatDate(apt.date)}\n⏰ Heure : ${apt.time}\n${baseInfo}`;
      default:
        return `⏰ Heure : ${apt.time}\n${baseInfo}\n✏️ Suivi : ${apt.status}`;
    }
  };

  const copyModalMessage = async () => {
    if (!confirmationModal.message) return;
    try {
      await navigator.clipboard.writeText(confirmationModal.message);
    } catch (error) {
      console.error('Error copying message:', error);
    }
  };

  const closeModal = () => {
    setConfirmationModal({ show: false, message: '', appointment: null });
  };

  const handleRescheduleConfirm = async (newDate: string, newTime: string) => {
    if (!rescheduleModal.appointment) return;

    try {
      await updateAppointmentDateTimeAndStatus(
        rescheduleModal.appointment.id,
        newDate,
        newTime,
        rescheduleModal.newStatus
      );

      await fetchAppointments();

      const updatedAppointment = {
        ...rescheduleModal.appointment,
        date: newDate,
        time: newTime,
        status: rescheduleModal.newStatus
      };

      const message = generateConfirmationMessage(updatedAppointment);
      setConfirmationModal({
        show: true,
        message,
        appointment: updatedAppointment
      });

      setRescheduleModal({ show: false, appointment: null, newStatus: '' });
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
    }
  };

  const handleRescheduleCancel = () => {
    setRescheduleModal({ show: false, appointment: null, newStatus: '' });
  };

  const statusStats = useMemo(() => {
    const stats = {
      Scheduled: 0,
      Confirmed: 0,
      Postponed: 0,
      Cancelled: 0,
      'No reply – VM/SMS': 0,
      Completed: 0,
      total: appointments.length
    };

    appointments.forEach((apt) => {
      const status = apt.status;
      if (status === 'Scheduled') stats.Scheduled++;
      else if (status === 'Confirmed') stats.Confirmed++;
      else if (status === 'Postponed') stats.Postponed++;
      else if (status === 'Cancelled') stats.Cancelled++;
      else if (status === 'No reply – VM/SMS' || status === 'No Answer') stats['No reply – VM/SMS']++;
      else if (status === 'Completed') stats.Completed++;
    });

    return stats;
  }, [appointments]);

  const filteredAppointments = useMemo(() => {
    let filtered = [...appointments];

    if (filterSdr !== 'all') {
      filtered = filtered.filter((apt) => apt.sdr_name === filterSdr);
    }

    if (filterClient !== 'all') {
      filtered = filtered.filter((apt) => apt.client_name === filterClient);
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter((apt) => apt.status === filterStatus);
    }

    return filtered;
  }, [appointments, filterSdr, filterClient, filterStatus]);

  const formatDate = (date: Date): string => {
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const months = [
      'janvier',
      'février',
      'mars',
      'avril',
      'mai',
      'juin',
      'juillet',
      'août',
      'septembre',
      'octobre',
      'novembre',
      'décembre'
    ];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const createdTodayStats = useMemo(() => {
    const total = appointmentsCreatedToday.length;
    const uniqueSdrs = new Set(appointmentsCreatedToday.map((a) => a.sdr_id).filter(Boolean)).size;
    const uniqueClients = new Set(appointmentsCreatedToday.map((a) => a.client_id)).size;

    return { total, uniqueSdrs, uniqueClients };
  }, [appointmentsCreatedToday]);

  const sdrCreatedStats = useMemo(() => {
    const statsMap = new Map<string, SdrCreatedStats>();

    appointmentsCreatedToday.forEach((apt) => {
      const key = apt.sdr_id || 'unknown';
      if (!statsMap.has(key)) {
        statsMap.set(key, {
          sdr_name: apt.sdr_name,
          sdr_id: apt.sdr_id || 'unknown',
          total_created: 0,
          clients: new Set<string>(),
          client_names: [],
          appointments: []
        });
      }

      const stats = statsMap.get(key)!;
      stats.total_created++;
      stats.clients.add(apt.client_id);
      if (!stats.client_names.includes(apt.client_name)) {
        stats.client_names.push(apt.client_name);
      }
      stats.appointments.push(apt);
    });

    return Array.from(statsMap.values()).sort((a, b) => b.total_created - a.total_created);
  }, [appointmentsCreatedToday]);

  const clientCreatedStats = useMemo(() => {
    const statsMap = new Map<string, ClientCreatedStats>();

    appointmentsCreatedToday.forEach((apt) => {
      if (!statsMap.has(apt.client_id)) {
        statsMap.set(apt.client_id, {
          client_name: apt.client_name,
          client_id: apt.client_id,
          total_created: 0,
          sdr_names: [],
          appointments: []
        });
      }

      const stats = statsMap.get(apt.client_id)!;
      stats.total_created++;
      if (apt.sdr_name && !stats.sdr_names.includes(apt.sdr_name)) {
        stats.sdr_names.push(apt.sdr_name);
      }
      stats.appointments.push(apt);
    });

    return Array.from(statsMap.values()).sort((a, b) => b.total_created - a.total_created);
  }, [appointmentsCreatedToday]);

  const allScheduledSdrsStats = useMemo(() => {
    const scheduledMap = new Map(scheduledSdrs.map((sdr) => [sdr.id, sdr]));
    const statsMap = new Map(sdrCreatedStats.map((stat) => [stat.sdr_id, stat]));

    const allSdrs: Array<{
      id: string;
      name: string;
      current: number;
      target: number;
      clients: string[];
      assignedClient?: string;
    }> = [];

    scheduledSdrs.forEach((sdr) => {
      const stats = statsMap.get(sdr.id);
      const clientsList = stats?.client_names || [];
      if (sdr.client_name && !clientsList.includes(sdr.client_name)) {
        clientsList.unshift(sdr.client_name);
      }
      allSdrs.push({
        id: sdr.id,
        name: sdr.name,
        current: stats?.total_created || 0,
        target: 3,
        clients: clientsList,
        assignedClient: sdr.client_name
      });
    });

    return allSdrs.sort((a, b) => {
      const percentA = (a.current / a.target) * 100;
      const percentB = (b.current / b.target) * 100;
      return percentB - percentA;
    });
  }, [scheduledSdrs, sdrCreatedStats]);

  const sdrOptions = useMemo(() => {
    return Array.from(new Set(appointments.map((apt) => apt.sdr_name))).sort();
  }, [appointments]);

  const clientOptions = useMemo(() => {
    return Array.from(new Set(appointments.map((apt) => apt.client_name))).sort();
  }, [appointments]);

  const statusOptions = [
    'Scheduled',
    'Confirmed',
    'Postponed',
    'Cancelled',
    'No reply – VM/SMS',
    'Completed'
  ];

  const handleFilterChange = (filterType: 'sdr' | 'client' | 'status', value: string) => {
    if (filterType === 'sdr') setFilterSdr(value);
    else if (filterType === 'client') setFilterClient(value);
    else if (filterType === 'status') setFilterStatus(value);
  };

  if (!profile || (profile.role !== 'admin' && profile.role !== 'super_admin')) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <ConfettiEffect trigger={showConfetti} />

      <div className="p-4 pb-12 max-w-[1800px] mx-auto space-y-4">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-3"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h1 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                <CalendarIcon className="h-5 w-5 text-cyan-500" />
                <span>Day to Day</span>
              </h1>

              <div className="flex items-center space-x-2">
                <motion.button
                  onClick={goToPreviousDay}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="p-1.5 bg-cyan-50 hover:bg-cyan-100 rounded-lg transition-colors"
                >
                  <ChevronLeft className="h-4 w-4 text-cyan-600" />
                </motion.button>

                <motion.div
                  key={selectedDate.toISOString()}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-base font-semibold text-gray-900 min-w-[200px] text-center"
                >
                  {formatDate(selectedDate)}
                </motion.div>

                <motion.button
                  onClick={goToNextDay}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="p-1.5 bg-cyan-50 hover:bg-cyan-100 rounded-lg transition-colors"
                >
                  <ChevronRight className="h-4 w-4 text-cyan-600" />
                </motion.button>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <motion.button
                onClick={() => setActiveTab('confirmations')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`px-4 py-2 rounded-lg font-semibold text-xs transition-all ${
                  activeTab === 'confirmations'
                    ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/30'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <div className="flex items-center space-x-1.5">
                  <CheckCircle className="h-3.5 w-3.5" />
                  <span>Confirmation de RDVs</span>
                </div>
              </motion.button>

              <motion.button
                onClick={() => setActiveTab('stats')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`px-4 py-2 rounded-lg font-semibold text-xs transition-all ${
                  activeTab === 'stats'
                    ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/30'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <div className="flex items-center space-x-1.5">
                  <TrendingUp className="h-3.5 w-3.5" />
                  <span>RDV pris ce jour</span>
                </div>
              </motion.button>
            </div>
          </div>
        </motion.div>

        {loading || loadingCreated ? (
          <div className="flex justify-center items-center py-20">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {activeTab === 'confirmations' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <KpiCard
                    title="Total RDVs"
                    value={statusStats.total}
                    icon={CalendarIcon}
                    color="cyan"
                    delay={0}
                  />
                  <KpiCard
                    title="Confirmés"
                    value={statusStats.Confirmed}
                    icon={CheckCircle}
                    color="green"
                    delay={0.1}
                  />
                  <KpiCard
                    title="Effectués"
                    value={statusStats.Completed}
                    icon={Target}
                    color="teal"
                    delay={0.2}
                  />
                  <KpiCard
                    title="Reportés"
                    value={statusStats.Postponed}
                    icon={Clock}
                    color="yellow"
                    delay={0.3}
                  />
                  <KpiCard
                    title="Annulés"
                    value={statusStats.Cancelled}
                    icon={X}
                    color="red"
                    delay={0.4}
                  />
                </div>

                <FiltersBar
                  isOpen={filtersOpen}
                  onToggle={() => setFiltersOpen(!filtersOpen)}
                  filters={{ sdr: filterSdr, client: filterClient, status: filterStatus }}
                  onFilterChange={handleFilterChange}
                  sdrOptions={sdrOptions}
                  clientOptions={clientOptions}
                  statusOptions={statusOptions}
                />

                <AppointmentList
                  appointments={filteredAppointments.map((apt) => ({
                    id: apt.id,
                    time: apt.time,
                    prospect_name: apt.prospect_name || 'N/A',
                    job_title: apt.job_title || undefined,
                    company_name: apt.company_name || undefined,
                    phone: apt.phone || undefined,
                    client_name: apt.client_name,
                    sdr_name: apt.sdr_name,
                    status: apt.status,
                    prospect_linkedin: apt.prospect_linkedin
                  }))}
                  onStatusChange={updateStatus}
                />
              </div>
            )}

            {activeTab === 'stats' && (
              <div className="space-y-4">
                <FeedBar
                  items={appointmentsCreatedToday.slice(0, 10).map((apt) => ({
                    id: apt.id,
                    text: `${apt.sdr_name} a pris un RDV avec ${apt.prospect_name || 'N/A'} chez ${
                      apt.company_name || 'N/A'
                    } pour ${apt.client_name}`,
                    time: new Date(apt.created_at).toLocaleTimeString('fr-FR', {
                      hour: '2-digit',
                      minute: '2-digit'
                    }),
                    type: 'new'
                  }))}
                />

                <div className="grid grid-cols-10 gap-4">
                  <div className="col-span-3">
                    <TeamProgressChart
                      current={createdTodayStats.total}
                      target={scheduledSdrs.length * 3}
                      activeSdrs={scheduledSdrs.length}
                    />
                  </div>

                  <div className="col-span-7">
                    <SdrProgressBars sdrs={allScheduledSdrsStats} />
                  </div>
                </div>

                <RdvListGrid
                  rdvs={appointmentsCreatedToday.map((apt) => ({
                    id: apt.id,
                    prospect_name: apt.prospect_name || 'N/A',
                    company_name: apt.company_name || undefined,
                    job_title: apt.job_title || undefined,
                    date: apt.date,
                    time: apt.time,
                    client_name: apt.client_name,
                    sdr_name: apt.sdr_name,
                    created_at: apt.created_at
                  }))}
                  onRdvClick={(rdv) => {
                    const fullRdv = appointmentsCreatedToday.find((apt) => apt.id === rdv.id);
                    if (fullRdv) {
                      setSelectedRdv(fullRdv);
                      setRdvDetailModalOpen(true);
                    }
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>

      <RescheduleModal
        isOpen={rescheduleModal.show}
        onClose={handleRescheduleCancel}
        onConfirm={handleRescheduleConfirm}
        currentDate={rescheduleModal.appointment?.date || ''}
        currentTime={rescheduleModal.appointment?.time || ''}
        prospectName={rescheduleModal.appointment?.prospect_name || ''}
        companyName={rescheduleModal.appointment?.company_name || ''}
      />

      <Modal isOpen={confirmationModal.show} onClose={closeModal} title="Message de confirmation">
        <div className="space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
              {confirmationModal.message}
            </pre>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              onClick={closeModal}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Fermer
            </button>
            <button
              onClick={copyModalMessage}
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors flex items-center space-x-2"
            >
              <Copy className="h-4 w-4" />
              <span>Copier le message</span>
            </button>
          </div>
        </div>
      </Modal>

      <RdvDetailModal
        isOpen={rdvDetailModalOpen}
        onClose={() => {
          setRdvDetailModalOpen(false);
          setSelectedRdv(null);
        }}
        rdv={
          selectedRdv
            ? {
                id: selectedRdv.id,
                prospect_name: selectedRdv.prospect_name || 'N/A',
                company_name: selectedRdv.company_name || undefined,
                job_title: selectedRdv.job_title || undefined,
                phone: selectedRdv.phone || undefined,
                email: undefined,
                date: selectedRdv.date,
                time: selectedRdv.time,
                client_name: selectedRdv.client_name,
                sdr_name: selectedRdv.sdr_name,
                status: selectedRdv.status,
                prospect_linkedin: selectedRdv.prospect_linkedin,
                created_at: selectedRdv.created_at
              }
            : null
        }
      />
    </div>
  );
};

export default ConfirmationRDVsPageNew;
